// R1c — CRM pipeline dashboard. Server-derived only — no fabricated numbers.
// Shows: opportunities grouped by stage, weighted forecast, average time-in-stage.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { Target } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["qualified","site_visit","estimating","proposal","negotiation","won","lost"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function daysBetween(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.round((Date.now() - then) / 86400000));
}

export default function Pipeline() {
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.get("/opportunities")
      .then((r) => setOpps(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const byStage = useMemo(() => {
    const out = {};
    for (const s of STAGES) out[s] = [];
    for (const o of opps) if (out[o.stage]) out[o.stage].push(o);
    return out;
  }, [opps]);

  const totals = useMemo(() => {
    const openStages = STAGES.filter((s) => s !== "won" && s !== "lost");
    const open = opps.filter((o) => openStages.includes(o.stage));
    return {
      count: open.length,
      value: open.reduce((a, o) => a + (o.expected_value || 0), 0),
      weighted: open.reduce((a, o) => a + (o.expected_value || 0) * (o.probability || 0), 0),
      won: opps.filter((o) => o.stage === "won").reduce((a, o) => a + (o.expected_value || 0), 0),
    };
  }, [opps]);

  return (
    <AppShell title="CRM · Pipeline" subtitle="Stages · value · weighted forecast · aging">
      {opps.length === 0 ? (
        <EmptyState
          icon={Target}
          title="Pipeline is empty"
          subtitle="Convert a Lead in /crm/leads to auto-create an Opportunity and populate this view."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pipeline-summary">
            <div className="bg-white border border-erf-borderCream rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">Open opps</div>
              <div className="font-mono text-2xl text-erf-dark mt-1" data-testid="pipeline-open-count">{totals.count}</div>
            </div>
            <div className="bg-white border border-erf-borderCream rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">Pipeline value</div>
              <div className="font-mono text-2xl text-erf-dark mt-1" data-testid="pipeline-value">{money(totals.value)}</div>
            </div>
            <div className="bg-white border border-erf-borderCream rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">Weighted forecast</div>
              <div className="font-mono text-2xl text-erf-dark mt-1" data-testid="pipeline-weighted">{money(totals.weighted)}</div>
            </div>
            <div className="bg-white border border-erf-borderCream rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">Won ($ closed)</div>
              <div className="font-mono text-2xl text-erf-dark mt-1" data-testid="pipeline-won">{money(totals.won)}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {STAGES.map((stage) => {
              const col = byStage[stage] || [];
              const sum = col.reduce((a, o) => a + (o.expected_value || 0), 0);
              return (
                <div
                  key={stage}
                  className="bg-erf-creamSecondary rounded-md p-2 border border-erf-borderCream"
                  data-testid={`pipeline-col-${stage}`}
                >
                  <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/70 px-1">{stage}</div>
                  <div className="text-[10px] font-mono text-erf-dark/60 mb-2 px-1">
                    {col.length} · {money(sum)}
                  </div>
                  <div className="space-y-2 min-h-[120px]">
                    {col.map((o) => {
                      const age = daysBetween(o.created_at);
                      return (
                        <Link
                          key={o.id}
                          to={o.source_lead_id ? `/crm/leads/${o.source_lead_id}` : "/crm/opportunities"}
                          className="block bg-white border border-erf-borderCream rounded p-2 hover:border-erf-gold/60"
                          data-testid={`pipeline-card-${o.id}`}
                        >
                          <div className="font-nav text-xs font-semibold text-erf-dark truncate">{o.name}</div>
                          <div className="font-mono text-[10px] text-erf-dark/70 mt-1">
                            {money(o.expected_value)} · {Math.round((o.probability || 0) * 100)}%
                          </div>
                          {age !== null && (
                            <div className="font-mono text-[10px] text-erf-dark/60">
                              age · {age}d
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </AppShell>
  );
}
